import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCatalog } from '../data/catalog';
import { addProductToCart } from '../data/cart';

// --- STYLES ---
const styles = {
  container: {
    position: 'fixed',
    bottom: '20px',
    left: '20px',
    zIndex: 9999,
    fontFamily: 'Nunito, sans-serif'
  },
  toggleButton: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#006400',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    cursor: 'pointer',
    border: 'none',
    fontSize: '24px',
    transition: 'transform 0.2s'
  },
  chatWindow: {
    position: 'absolute',
    bottom: '80px',
    left: '0',
    width: '350px',
    height: '500px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '1px solid #eaeaea'
  },
  header: {
    backgroundColor: '#006400',
    color: 'white',
    padding: '15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 'bold'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer'
  },
  messages: {
    flex: 1,
    padding: '15px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    backgroundColor: '#f9f9f9'
  },
  message: (isUser) => ({
    maxWidth: '85%',
    padding: '10px 14px',
    borderRadius: '12px',
    alignSelf: isUser ? 'flex-end' : 'flex-start',
    backgroundColor: isUser ? '#f97316' : '#e2e8f0',
    color: isUser ? 'white' : '#1e293b',
    fontSize: '0.9rem',
    lineHeight: '1.4'
  }),
  inputArea: {
    padding: '10px',
    borderTop: '1px solid #eaeaea',
    display: 'flex',
    gap: '10px',
    backgroundColor: 'white'
  },
  input: {
    flex: 1,
    padding: '10px',
    borderRadius: '20px',
    border: '1px solid #ccc',
    outline: 'none',
    fontSize: '0.9rem'
  },
  sendBtn: {
    backgroundColor: '#f97316',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  }
};

const SYSTEM_PROMPT = `Tu es l'assistant virtuel de l'épicerie Almoune, spécialisée dans les produits libanais et orientaux à Evry-Courcouronnes.
Règles strictes :
1. Tu dois IMPÉRATIVEMENT refuser de parler de tout sujet qui n'est pas lié à l'épicerie, à la cuisine libanaise/orientale, ou aux produits du magasin (comme la météo, la politique, etc.). Ramène toujours la conversation aux produits.
2. Si le client te demande une recette, donne-lui la recette et suggère-lui les produits nécessaires de notre catalogue.
3. Tu as accès à un outil 'add_to_cart' pour ajouter des produits au panier. Tu ne dois l'utiliser QUE si le client t'a expressément demandé d'ajouter le produit OU si tu lui as proposé gentiment et qu'il a accepté (ex: "Voulez-vous que j'ajoute le Zaatar à votre panier ?"). Ne force pas la vente.
`;

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Bonjour ! Je suis l\'assistant Almoune. Comment puis-je vous aider avec nos produits libanais et orientaux aujourd\'hui ?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  
  const { session } = useAuth();
  const isAuth = !!session;

  const location = useLocation();
  const isAdminRoute =
    location.pathname.startsWith("/MyAccount") ||
    location.pathname.startsWith("/MyAcconut") ||
    location.pathname.startsWith("/Pilotage");

  const [cartHasItems, setCartHasItems] = useState(() => {
    const raw = window.localStorage.getItem("almoune_cart_cache");
    if (!raw) return false;
    try {
      return JSON.parse(raw).length > 0;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const updateCartStatus = () => {
      const raw = window.localStorage.getItem("almoune_cart_cache");
      let hasItems = false;
      if (raw) {
        try { hasItems = JSON.parse(raw).length > 0; } catch {}
      }
      setCartHasItems(hasItems);
    };
    window.addEventListener("cart-updated", updateCartStatus);
    return () => window.removeEventListener("cart-updated", updateCartStatus);
  }, []);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const checkLimits = () => {
    const today = new Date().toISOString().slice(0, 10);
    if (!isAuth) {
      const anonData = JSON.parse(localStorage.getItem('almoune_chat_anon') || '{"count": 0, "date": ""}');
      if (anonData.count >= 10) return "LIMIT_ANON";
      if (anonData.count >= 7) return "WARN_ANON"; 
      return "OK_ANON";
    } else {
      const authData = JSON.parse(localStorage.getItem('almoune_chat_auth') || '{"count": 0, "date": ""}');
      if (authData.date !== today) {
        authData.count = 0;
        authData.date = today;
      }
      if (authData.count >= 30) return "LIMIT_AUTH";
      return "OK_AUTH";
    }
  };

  const incrementLimit = () => {
    const today = new Date().toISOString().slice(0, 10);
    if (!isAuth) {
      const anonData = JSON.parse(localStorage.getItem('almoune_chat_anon') || '{"count": 0, "date": ""}');
      anonData.count += 1;
      localStorage.setItem('almoune_chat_anon', JSON.stringify(anonData));
    } else {
      const authData = JSON.parse(localStorage.getItem('almoune_chat_auth') || '{"count": 0, "date": ""}');
      if (authData.date !== today) {
        authData.count = 0;
        authData.date = today;
      }
      authData.count += 1;
      localStorage.setItem('almoune_chat_auth', JSON.stringify(authData));
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const limitStatus = checkLimits();
    if (limitStatus === "LIMIT_ANON") {
      setMessages(prev => [...prev, { role: 'user', content: input }, { role: 'assistant', content: 'Vous avez atteint la limite de 10 messages. Veuillez vous connecter à votre compte pour continuer à discuter avec moi !' }]);
      setInput('');
      return;
    } else if (limitStatus === "LIMIT_AUTH") {
      setMessages(prev => [...prev, { role: 'user', content: input }, { role: 'assistant', content: 'Vous avez atteint votre limite de 30 messages pour aujourd\'hui. À demain !' }]);
      setInput('');
      return;
    }

    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const catalog = getCatalog();
      
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      if (!apiKey) {
         setMessages(prev => [...prev, { role: 'assistant', content: 'Clé API OpenAI non configurée. Impossible de vous répondre.' }]);
         setIsLoading(false);
         return;
      }

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: SYSTEM_PROMPT },
            ...messages.map(m => ({ role: m.role, content: m.content })),
            userMsg
          ],
          tools: [
            {
              type: "function",
              function: {
                name: "add_to_cart",
                description: "Ajoute un produit au panier du client en utilisant l'ID exact du produit.",
                parameters: {
                  type: "object",
                  properties: {
                    productId: { type: "string", description: "L'ID du produit à ajouter (doit être un ID valide du catalogue)" },
                    quantity: { type: "number", description: "Quantité à ajouter (par défaut 1)" }
                  },
                  required: ["productId"]
                }
              }
            },
            {
              type: "function",
              function: {
                name: "search_catalog",
                description: "Recherche dans le catalogue des produits Almoune.",
                parameters: {
                  type: "object",
                  properties: {
                    query: { type: "string", description: "Le terme de recherche (ex: zaatar, houmous)" }
                  },
                  required: ["query"]
                }
              }
            }
          ]
        })
      });

      const data = await response.json();
      
      if (data.choices && data.choices[0].message) {
        const msg = data.choices[0].message;
        
        if (msg.tool_calls) {
          let finalReply = "";
          for (const toolCall of msg.tool_calls) {
            if (toolCall.function.name === 'search_catalog') {
              const args = JSON.parse(toolCall.function.arguments);
              const results = catalog.filter(p => (p.name||"").toLowerCase().includes(args.query.toLowerCase())).slice(0,5);
              finalReply = "Voici ce que j'ai trouvé : " + results.map(r => `${r.name} (${r.price}€, ID: ${r.id})`).join(', ');
              if (results.length === 0) finalReply = "Désolé, je n'ai pas trouvé de produit correspondant à '" + args.query + "'.";
            } else if (toolCall.function.name === 'add_to_cart') {
              const args = JSON.parse(toolCall.function.arguments);
              const prod = catalog.find(p => p.id === args.productId);
              if (prod) {
                await addProductToCart(prod, args.quantity || 1);
                finalReply = `J'ai bien ajouté ${args.quantity || 1} x ${prod.name} à votre panier.`;
              } else {
                finalReply = "Désolé, l'ID du produit est invalide.";
              }
            }
          }
          setMessages(prev => [...prev, { role: 'assistant', content: finalReply }]);
        } else {
          setMessages(prev => [...prev, { role: 'assistant', content: msg.content }]);
        }

        incrementLimit();
        const newLimitStatus = checkLimits();
        if (!isAuth && newLimitStatus === "WARN_ANON") {
            setMessages(prev => [...prev, { role: 'assistant', content: '💡 Attention : il ne vous reste que quelques messages gratuits. Pensez à vous connecter !' }]);
        }
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Désolé, une erreur est survenue.' }]);
      }
    } catch (err) {
      console.error(err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Désolé, l\'assistant est indisponible pour le moment.' }]);
    }
    setIsLoading(false);
  };

  if (isAdminRoute) return null;
  if (!isAuth && !cartHasItems) return null;

  return (
    <div style={styles.container}>
      {isOpen && (
        <div style={styles.chatWindow}>
          <div style={styles.header}>
            <span>Almoune Assistant</span>
            <button style={styles.closeBtn} onClick={() => setIsOpen(false)}>&times;</button>
          </div>
          <div style={styles.messages}>
            {messages.map((m, i) => (
              <div key={i} style={styles.message(m.role === 'user')}>
                {m.content}
              </div>
            ))}
            {isLoading && <div style={styles.message(false)}>...</div>}
            <div ref={messagesEndRef} />
          </div>
          <div style={styles.inputArea}>
            <input 
              style={styles.input} 
              value={input} 
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Posez votre question..."
            />
            <button style={styles.sendBtn} onClick={handleSend} disabled={isLoading}>
              <i className="fa fa-paper-plane" />
            </button>
          </div>
        </div>
      )}
      {!isOpen && (
        <button style={styles.toggleButton} onClick={() => setIsOpen(true)}>
          <i className="fa fa-comments" />
        </button>
      )}
    </div>
  );
};

export default ChatbotWidget;
